import React, { useCallback, useEffect, useState } from 'react';
import { Archive, Download, RefreshCw } from 'lucide-react';
import { useCanPerform } from '../../../hooks/useCanPerform';
import ExportService from '../../../services/admin/export.service';
import { describeApiError } from '../../../utils/apiErrors';
import type { ExportJob } from '../../../dtos/admin/export.dto';
import ExportDialog from './ExportDialog';

const STATUS_LABEL: Record<ExportJob['status'], string> = {
  queued: 'Queued',
  running: 'Preparing',
  complete: 'Ready',
  failed: 'Failed',
};

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * Organization Account: agency-wide export (DAT-02) and the export history (patient and agency
 * exports, DAT-01). Owner and admin only. Also shown on the export-only suspended page (DAT-04).
 */
const DataExportSection: React.FC = () => {
  const canExportAgency = useCanPerform('export:agency');
  const canExportPatient = useCanPerform('export:patient');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const visible = canExportAgency || canExportPatient;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setJobs(await ExportService.list());
    } catch (err) {
      setError(describeApiError(err, 'Unable to load the export history.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visible) void load();
  }, [visible, load]);

  if (!visible) return null;

  const download = async (job: ExportJob) => {
    setError(null);
    try {
      await ExportService.download(job.exportId);
    } catch (err) {
      setError(describeApiError(err, 'The download link could not be created.'));
    }
  };

  return (
    <section id="data-export" aria-labelledby="data-export-heading" className="bg-white rounded-xl border border-slate-200">
      <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-slate-200">
        <div className="flex items-center gap-2"><Archive size={17} className="text-slate-600" /><h2 id="data-export-heading" className="text-base font-semibold text-slate-900">Data Export</h2></div>
        <p className="text-sm text-slate-500 mt-1">Export your agency&apos;s records as PDF and CSV files. Exports stay available while the account is read-only or suspended.</p>
      </div>
      <div className="p-3 sm:p-4">
        {canExportAgency && (
          <button type="button" onClick={() => setDialogOpen(true)} className="inline-flex items-center gap-2 rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700">
            <Archive size={16} /> Export all agency data
          </button>
        )}
        <div className="mt-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Recent exports</h3>
          <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden="true" /> Refresh
          </button>
        </div>
        {error && <p className="mt-2 text-sm text-red-700" role="alert">{error}</p>}
        {jobs.length === 0 && !loading ? (
          <p className="mt-2 text-sm text-slate-500">No exports yet.</p>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-3 font-medium">Requested</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Purpose</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 font-medium"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.exportId} className="border-b border-slate-100 last:border-0" data-testid="export-row">
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-700">{formatDateTime(job.createdAt)}</td>
                    <td className="py-2 pr-3 text-slate-700">{job.scopeLabel}</td>
                    <td className="py-2 pr-3 text-slate-700 max-w-xs truncate" title={job.purpose}>{job.purpose}</td>
                    <td className="py-2 pr-3 text-slate-700">{job.expired ? 'Expired' : STATUS_LABEL[job.status]}</td>
                    <td className="py-2 text-right">
                      {job.canDownload && (
                        <button type="button" onClick={() => download(job)} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-800 hover:bg-slate-50">
                          <Download size={13} aria-hidden="true" /> Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ExportDialog open={dialogOpen} scope="agency" onClose={() => { setDialogOpen(false); void load(); }} onFinished={() => void load()} />
    </section>
  );
};

export default DataExportSection;
